import React, { useState, useEffect } from "react";
import { Button, Select, SelectItem, SelectItemGroup } from "carbon-components-react";
import { AddFilled32, Edit32, TrashCan32 } from "@carbon/icons-react";
import { groupBy } from "lodash";
import { TaskConditionalShape, TaskConfigParamOptions } from "@eave/shared/types/api";

import getHandlers from "web_modules/shared/FormHelpers/eventHandlers";

import TaskFuncParams from "./TaskFuncParams";
import { getDefByKey, getSchema, TaskConfigFilterType, TaskConfigSchemaDef } from "./shared";

const styles = require("./styles.module.scss");

interface TaskClauseProps {
  disabled?: boolean;
  taskOptions: TaskConfigParamOptions;
  condition?: TaskConditionalShape;
  setCondition: Function;

  typeOfClause: string;

  setDisableModalSubmit: Function;
}

const TaskConditional: React.FC<TaskClauseProps> = ({
  disabled = false,
  taskOptions,
  condition = null,
  setCondition,
  typeOfClause,
  setDisableModalSubmit,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localFormState, setLocalFormState] = useState(null);
  const [invalidFields, setInvalidFields] = useState([]);

  const schema = getSchema(taskOptions);
  const taskConfigDefinition: TaskConfigSchemaDef =
    localFormState && localFormState.func_name
      ? getDefByKey(schema, localFormState.func_name)
      : null;

  // If user chose a non-boolean based func_name, require that all values be
  const isInvalid = (): Boolean => {
    if (!localFormState || !localFormState.func_name || !taskConfigDefinition) {
      return true;
    }

    // Boolean inputs don't need additional parameters
    if (taskConfigDefinition.type === TaskConfigFilterType.BOOL) {
      return false;
    }

    // General validity check
    if (localFormState === null || localFormState.params === undefined) {
      return true;
    }

    // If single-value based comparisons, just check to ensure the comparisonKey is defined
    if (
      taskConfigDefinition.type === TaskConfigFilterType.DATE ||
      taskConfigDefinition.type === TaskConfigFilterType.ENUM ||
      taskConfigDefinition.type === TaskConfigFilterType.TEXT
    ) {
      return (
        localFormState.params[taskConfigDefinition.comparisonKey] === undefined ||
        localFormState.params[taskConfigDefinition.comparisonKey] === ""
      );
    }

    // If numerical comparisons, ensure the comparisonKey AND decimal_operator (>, <, etc) is defined
    if (
      taskConfigDefinition.type === TaskConfigFilterType.AMOUNT ||
      taskConfigDefinition.type === TaskConfigFilterType.CURRENCY
    ) {
      return (
        localFormState.params[taskConfigDefinition.comparisonKey] === undefined ||
        localFormState.params[taskConfigDefinition.comparisonKey] === "" ||
        localFormState.params.decimal_operator === undefined
      );
    }

    return false;
  };

  useEffect(() => {
    if (isEditing) {
      setDisableModalSubmit(true);
      return;
    }
    setDisableModalSubmit(false);
  }, [isEditing, setDisableModalSubmit]);

  // If the parent's condition changes, it should override any localFormState
  useEffect(() => {
    setLocalFormState(condition);
  }, [condition]);

  const { handleSelectChange } = getHandlers(
    localFormState,
    state => {
      // Reset `params` when changing func_name
      const withoutParams = { ...state };
      delete withoutParams.params;
      setLocalFormState(withoutParams);
    },
    invalidFields,
    setInvalidFields,
  );

  // Grouping by the type allows us to easily add <Select> groups for clarity
  const typesToFunctionNames = groupBy(
    taskOptions.function_names.map(fn => getDefByKey(schema, fn)),
    "type",
  );

  return (
    <div className={styles.taskConditionalWrapper}>
      <Select
        disabled={!isEditing && disabled}
        id="task-function-name"
        name="func_name"
        value={(localFormState && localFormState.func_name) || ""}
        invalid={invalidFields.includes("func_name")}
        invalidText="Select a valid function (AND/OR)"
        labelText="Condition: Function Name *"
        onChange={e => {
          handleSelectChange(e, "func_name");

          const { target } = e;
          const { value } = target;

          // If resetting the <Select>, we're not editing.
          if (value === "") {
            setIsEditing(false);
            return;
          }

          // If the func_name requires parameters, we've begun editing
          const selectedDefinition = getDefByKey(schema, value);
          if (
            selectedDefinition &&
            selectedDefinition.comparisonKey &&
            selectedDefinition.comparisonKey !== ""
          ) {
            setIsEditing(true);
          }
        }}
        size="sm"
      >
        <SelectItem text="Select a function" value="" />
        {Object.entries(typesToFunctionNames).map(([type, functions]) => (
          <>
            <SelectItemGroup label={type}>
              {functions.map(op => (
                <SelectItem
                  key={`${typeOfClause}-${op.func_name}`}
                  text={op.displayText}
                  value={op.func_name}
                />
              ))}
            </SelectItemGroup>
          </>
        ))}
      </Select>

      {taskConfigDefinition && (
        <TaskFuncParams
          taskOptions={taskOptions}
          disabled={disabled && !isEditing}
          params={(localFormState && localFormState.params) || {}}
          setParams={params => setLocalFormState({ ...localFormState, params })}
          taskConfigDefinition={taskConfigDefinition}
        />
      )}

      {disabled && !isEditing && (
        <>
          <Button
            renderIcon={Edit32}
            iconDescription="Edit condition"
            hasIconOnly
            kind="secondary"
            onClick={() => setIsEditing(true)}
            tooltipAlignment="center"
            tooltipPosition="top"
          />
          <Button
            renderIcon={TrashCan32}
            iconDescription="Delete condition"
            hasIconOnly
            kind="danger"
            onClick={() => setCondition(null)}
            tooltipAlignment="center"
            tooltipPosition="top"
          />
        </>
      )}

      {(isEditing || !disabled) && (
        <Button
          renderIcon={AddFilled32}
          iconDescription="Save condition"
          hasIconOnly
          disabled={!localFormState || !localFormState.func_name || isInvalid()}
          onClick={() => {
            setCondition(localFormState);

            // reset
            setLocalFormState(null);
            setIsEditing(false);
          }}
          tooltipAlignment="center"
          tooltipPosition="top"
        />
      )}
    </div>
  );
};

export default TaskConditional;
